/**
 * "Move to…": a folder picker (searched by name, recent folders first, the space's items at the root, and a way to
 * make a new folder right there), then the move. Items keep their sharing unless asked to match the folder's; a
 * folder meeting one of its name where it goes is renamed ("Acme (2)") or merged into it, as asked, after saying
 * what a merge will do.
 */
import { useState } from "preact/hooks";
import { ApiError, api } from "../../api/client.ts";
import type {
  BulkResult,
  FolderCounts,
  FolderList,
  FolderOut,
  ItemFilter,
  Job,
  OnClash,
  SpaceOut,
} from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { patchItem } from "../../lib/data.ts";
import { rememberFolder, type Sharing, sameSharing, useFolderIndex, whatInside } from "../../lib/folders.ts";
import { spaceApi } from "../../lib/paths.ts";
import { bump, useAction } from "../../lib/resource.ts";
import { fold } from "../../lib/text.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Checkbox, Form } from "../../ui/form.tsx";
import { NewFolderIcon } from "../../ui/icons.tsx";
import { NewFolderForm } from "./name.tsx";
import { folderOffers, ROOT } from "./offers.tsx";

const NAME_TAKEN = "name_taken";

/** What's moved: items (by id, or every one a list's filter matches), or a folder. */
export type Moving =
  | {
      kind: "items";
      ids: readonly string[];
      /** Every match of a list (`ids` aside): a job on the server. */
      filter?: ItemFilter | undefined;
      /** Their sharing, when known (one item's, a page's rows): whether to offer matching the folder's. */
      items?: readonly (Sharing & { id: string; ver?: string | undefined })[] | undefined;
      /** From a triage area: filing them takes them out of it. */
      triage?: boolean;
      /** Folders picked with them (a list's rows): each moved too, renamed ("Acme (2)") beside one of its name. */
      folders?: readonly { id: string; ver?: string | undefined; name: string }[] | undefined;
    }
  | { kind: "folder"; folder: Pick<FolderOut, "id" | "ver" | "name"> & { counts: FolderCounts } };

/** What a move did, for the list it was asked from. */
export type Moved = { job: Job } | { to: string | null };

/** The picked folder (`root`: none), named. */
interface Target {
  value: string;
  label: string;
}

/** A clash moving a folder: the folder of its name there, and what merging would do. */
interface Clash {
  twin: string;
  shared: string[];
}

/** The names of `id`'s subfolders. */
async function subNames(space: SpaceOut, id: string): Promise<string[]> {
  const l = await api.get<FolderList>(spaceApi(space, "/folders"), { parent: id });
  return l.data.map((f) => f.name);
}

/** Moves the folders picked with items into `to` (null: the root), each renamed beside one of its name. */
async function moveFolders(
  space: SpaceOut,
  folders: NonNullable<Extract<Moving, { kind: "items" }>["folders"]>,
  to: string | null,
) {
  for (const f of folders) {
    await api.patch<unknown>(spaceApi(space, `/folders/${f.id}`), {
      parent: to,
      ...(f.ver ? { ver: f.ver } : {}),
      on_clash: "rename",
    });
  }
}

/** Moves items to folder `to.id` (null: the root), named `to.name`, as `moving` says; `to.take`: taking its sharing. */
async function moveItems(
  space: SpaceOut,
  moving: Extract<Moving, { kind: "items" }>,
  to: { id: string | null; name: string; take: boolean },
): Promise<Moved> {
  const { name, take } = to;
  const folders = moving.folders ?? [];
  if (folders.length > 0) {
    await moveFolders(space, folders, to.id);
    if (moving.ids.length === 0 && !moving.filter) {
      toast(t("folders.movedSelection", { name }));
      return { to: to.id };
    }
  }
  const set = { folder: to.id, take_sharing: take, ...(moving.triage ? { triage: false } : {}) };
  const one = moving.items?.length === 1 && moving.ids.length === 1 ? moving.items[0] : undefined;
  if (one && !moving.filter) {
    await patchItem(space, one, set);
    toast(t("folders.movedTo", { name }));
    return { to: to.id };
  }
  const path = spaceApi(space, "/items/bulk");
  if (moving.filter) {
    return { job: await api.post<Job>(path, { filter: moving.filter, set }) };
  }
  const r = await api.post<BulkResult>(path, { ids: moving.ids, set });
  toast(folders.length > 0 ? t("folders.movedSelection", { name }) : t("folders.moved", { count: r.matched, name }));
  if (r.skipped.length > 0) {
    toast(t("bulk.skipped", { count: r.skipped.length }), "error");
  }
  return { to: to.id };
}

/** The form: where to, matching its sharing (items), and the move; then, a folder meeting its namesake, how. */
function MoveForm(p: { space: SpaceOut; moving: Moving; onClose: () => void; onDone: (m: Moved) => void }) {
  const { space, moving } = p;
  const index = useFolderIndex(space);
  const [target, setTarget] = useState<Target | null>(null);
  const [take, setTake] = useState(false);
  const [making, setMaking] = useState(false);
  const [clash, setClash] = useState<Clash | null>(null);
  const away = moving.kind === "folder" ? [moving.folder.id] : (moving.folders ?? []).map((f) => f.id);
  const to = target && target.value !== ROOT ? target.value : null;
  const there = to ? index.data?.byId.get(to) : undefined;
  // Items whose sharing isn't the folder's (unknown ones might be): matching it is offered.
  const differs =
    moving.kind === "items" &&
    there !== undefined &&
    (moving.items === undefined || moving.items.some((i) => !sameSharing(i, there)));
  const finish = (m: Moved) => {
    if (to) {
      rememberFolder(space, to);
    }
    bump();
    p.onClose();
    p.onDone(m);
  };
  const [run, busy] = useAction(async (how?: OnClash) => {
    if (target === null) {
      return;
    }
    if (moving.kind === "items") {
      finish(await moveItems(space, moving, { id: to, name: target.label, take: differs && take }));
      return;
    }
    const path = spaceApi(space, `/folders/${moving.folder.id}`);
    try {
      const f = await api.patch<FolderOut>(path, {
        parent: to,
        ver: moving.folder.ver,
        ...(how ? { on_clash: how } : {}),
      });
      toast(f.merged_into ? t("folders.merged", { name: f.name }) : t("folders.movedFolder", { name: target.label }));
      if (f.left_in_place) {
        toast(t("folders.leftInPlace"));
      }
      finish({ to });
    } catch (e) {
      if (e instanceof ApiError && e.code === NAME_TAKEN && !how && typeof e.info["folder"] === "string") {
        const twin = e.info["folder"];
        const [mine, theirs] = await Promise.all([subNames(space, moving.folder.id), subNames(space, twin)]);
        const names = new Set(theirs.map(fold));
        setClash({ twin, shared: mine.filter((n) => names.has(fold(n))) });
        return;
      }
      throw e;
    }
  });
  if (clash && moving.kind === "folder" && target) {
    const what = whatInside(moving.folder.counts);
    return (
      <div class="flex flex-col gap-3 text-sm text-zinc-700">
        <p>{t("folders.clash", { name: moving.folder.name, place: target.label })}</p>
        <p>
          {what
            ? t("folders.mergeWhat", { what, name: moving.folder.name })
            : t("folders.mergeEmpty", { name: moving.folder.name })}
        </p>
        {clash.shared.length > 0 ? (
          <ul class="list-disc pl-5">
            {clash.shared.map((n) => (
              <li key={n}>{t("folders.mergeSub", { name: n })}</li>
            ))}
          </ul>
        ) : null}
        <DialogActions>
          <Button answer="cancel" onClick={p.onClose}>
            {t("common.cancel")}
          </Button>
          <Button busy={busy} onClick={() => run("rename")}>
            {t("folders.keepBoth")}
          </Button>
          <Button answer="confirm" variant="primary" busy={busy} onClick={() => run("merge")}>
            {t("folders.merge")}
          </Button>
        </DialogActions>
      </div>
    );
  }
  const place = target?.label ?? t("folders.root");
  if (making) {
    return (
      <div class="flex flex-col gap-4">
        <p class="text-sm text-zinc-600">{t("folders.newIn", { place })}</p>
        <NewFolderForm
          space={space}
          parent={to}
          onCancel={() => setMaking(false)}
          onMade={(f) => {
            index.reload();
            setTarget({ value: f.id, label: f.name });
            setMaking(false);
          }}
        />
      </div>
    );
  }
  return (
    <Form class="flex flex-col gap-4" onSubmit={() => run()}>
      <Combobox
        key={index.data ? "ready" : "loading"}
        label={t("folders.moveTarget")}
        placeholder={t("folders.search")}
        inline={true}
        picked={target?.value ?? ""}
        search={(q) => Promise.resolve(folderOffers(index.data, space, q, away))}
        onSelect={(o) => setTarget({ value: o.value, label: o.label })}
      />
      {differs ? (
        <Checkbox label={t("folders.takeSharing", { name: there?.name ?? "" })} checked={take} onCheck={setTake} />
      ) : null}
      <DialogActions>
        <span class="mr-auto">
          <Button onClick={() => setMaking(true)} title={t("folders.newIn", { place })}>
            <NewFolderIcon size={16} />
            {t("folders.new")}
          </Button>
        </span>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy} disabled={target === null}>
          {t("folders.moveHere")}
        </Button>
      </DialogActions>
    </Form>
  );
}

/** "Move to…" for `moving`, titled `title`; `onDone` says what came of it (a job to follow, or where they went). */
export function MoveDialog(p: {
  space: SpaceOut;
  open: boolean;
  title: string;
  moving: Moving;
  onClose: () => void;
  onDone: (m: Moved) => void;
}) {
  return (
    <Dialog open={p.open} onClose={p.onClose} title={p.title}>
      {p.open ? <MoveForm space={p.space} moving={p.moving} onClose={p.onClose} onDone={p.onDone} /> : null}
    </Dialog>
  );
}
